/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IAttributeListItem, ISearchAssignmentItem } from '../../models';
import { buildEntityCompositeKey, FilteredEntityIds } from '../../utils';
import { IAttributeState } from '../state';

export const getAttributeState = createFeatureSelector<IAttributeState>('attribute');

export const getAttributeItems = createSelector(getAttributeState, (state: IAttributeState): IAttributeListItem[] => state.items);

export const getAttributeError = createSelector(getAttributeState, (state: IAttributeState): boolean => state.error);

export const getAttributeLoading = createSelector(getAttributeState, (state: IAttributeState): boolean => state.loading);

export const getSelectedAttributes = createSelector(
  getAttributeState,
  (state: IAttributeState): IAttributeListItem[] => state.selectedAttributes
);

export const getAssignments = createSelector(getAttributeState, (state: IAttributeState): ISearchAssignmentItem[] => state.assignments);

export const getAssignmentsLoading = createSelector(getAttributeState, (state: IAttributeState): boolean => state.assignmentsLoading);

export const getAssignmentsError = createSelector(getAttributeState, (state: IAttributeState): boolean => state.assignmentsError);

export const getLastSearchedEntityKey = createSelector(getAttributeState, (state: IAttributeState): string => state.lastSearchedEntityKey);

export const getFilteredEntityIds = createSelector(
  getAssignments,
  getSelectedAttributes,
  getAssignmentsLoading,
  (assignments: ISearchAssignmentItem[], selected: IAttributeListItem[], loading: boolean): FilteredEntityIds => {
    if (selected.length === 0 || loading) {
      return null;
    }

    const activeValues = new Set<string>();
    for (const attr of selected) {
      for (const value of attr.stringValues) {
        activeValues.add(`${attr.attributeId}:${value}`);
      }
    }

    const entityIds = new Set<string>();
    for (const assignment of assignments) {
      if (activeValues.has(`${assignment.attributeId}:${assignment.stringValue}`)) {
        entityIds.add(buildEntityCompositeKey(assignment.entityType, assignment.entityId));
      }
    }

    return entityIds;
  }
);

export const getLegacyMapToUuid = createSelector(
  getAttributeState,
  (state: IAttributeState): Record<string, string> => state.legacyMapToUuid
);

export const getUuidMapToLegacy = createSelector(
  getAttributeState,
  (state: IAttributeState): Record<string, string> => state.uuidMapToLegacy
);
